import { useState } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  X,
  ChevronRight,
  Moon,
  Activity,
  Heart,
  Wifi,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useHealthAlerts,
  AlertType,
  AlertSeverity,
  HealthAlert,
} from '@/hooks/use-health-alerts';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface HealthAlertsCardProps {
  organizationId: string;
  clientId?: string;
  className?: string;
  maxAlerts?: number;
  showClientName?: boolean;
  /** If true, render nothing when there are zero active alerts (avoids
   *  taking up dashboard real estate for orgs without wearable integrations). */
  hideWhenEmpty?: boolean;
}

const ALERT_ICONS: Record<AlertType, React.ComponentType<{ className?: string }>> = {
  low_readiness: Activity,
  sleep_decline: Moon,
  hrv_drop: Heart,
  missed_sync: Wifi,
  weight_change: Activity,
  recovery_needed: Activity,
  streak_at_risk: Activity,
  inactivity: Clock,
};

const SEVERITY_CONFIG: Record<
  AlertSeverity,
  { icon: React.ComponentType<{ className?: string }>; bgColor: string; textColor: string }
> = {
  critical: {
    icon: AlertTriangle,
    bgColor: 'bg-destructive/10 border-destructive/30',
    textColor: 'text-destructive',
  },
  warning: {
    icon: AlertCircle,
    bgColor: 'bg-warning/10 border-warning/30',
    textColor: 'text-warning',
  },
  info: {
    icon: Info,
    bgColor: 'bg-info/10 border-info/30',
    textColor: 'text-info',
  },
};

function AlertItem({
  alert,
  showClientName,
  onAcknowledge,
  onDismiss,
}: {
  alert: HealthAlert;
  showClientName?: boolean;
  onAcknowledge: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  const config = SEVERITY_CONFIG[alert.severity];
  const AlertIcon = ALERT_ICONS[alert.alertType] || AlertCircle;
  const SeverityIcon = config.icon;

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border',
        config.bgColor,
        alert.acknowledgedAt && 'opacity-60'
      )}
    >
      <div className={cn('mt-0.5', config.textColor)}>
        <AlertIcon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {showClientName && alert.clientName && (
            <span className="font-medium text-sm">{alert.clientName}</span>
          )}
          <Badge variant="outline" className={cn('text-xs', config.textColor)}>
            <SeverityIcon className="h-3 w-3 mr-1" />
            {alert.severity}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
          </span>
        </div>
        <p className="text-sm text-foreground">{alert.message}</p>
        {alert.acknowledgedAt && (
          <p className="text-xs text-muted-foreground mt-1">
            Acknowledged {formatDistanceToNow(new Date(alert.acknowledgedAt), { addSuffix: true })}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1">
        {!alert.acknowledgedAt && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onAcknowledge(alert.id)}
            title="Acknowledge"
          >
            <Check className="h-4 w-4 text-success" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => onDismiss(alert.id)}
          title="Dismiss"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    </div>
  );
}

export function HealthAlertsCard({
  organizationId,
  clientId,
  className,
  maxAlerts = 5,
  showClientName = true,
  hideWhenEmpty = false,
}: HealthAlertsCardProps) {
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const { hasFeature } = useFeatureAccess();
  const {
    alerts,
    loading,
    error,
    featureAvailable,
    acknowledgeAlert,
    dismissAlert,
    criticalCount,
    warningCount,
    totalCount,
  } = useHealthAlerts(organizationId, {
    includeAcknowledged: showAcknowledged,
    clientId,
  });

  // FEATURE GATE: hides until health_alerts table exists in DB.
  // Also hides for users whose tier doesn't include the health_hub feature.
  // The underlying hook + component code stays intact — when the migration
  // is applied in production, this will automatically start rendering again.
  if (!hasFeature('health_hub') || !featureAvailable) {
    return null;
  }

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const displayedAlerts = alerts.slice(0, maxAlerts);
  const hasMore = alerts.length > maxAlerts;

  // Caller can opt to render nothing when there are zero alerts (used by
  // dashboard surface that doesn't want a "no active alerts" empty card
  // eating space for orgs without wearable integrations)
  if (hideWhenEmpty && displayedAlerts.length === 0) {
    return null;
  }

  return (
    <Card className={cn('border', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <AlertTriangle
              className={cn(
                'h-5 w-5',
                criticalCount > 0 ? 'text-destructive' : warningCount > 0 ? 'text-warning' : 'text-muted-foreground'
              )}
            />
            Health Alerts
            {totalCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {totalCount}
              </Badge>
            )}
          </span>
          <div className="flex items-center gap-2">
            {criticalCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {criticalCount} Critical
              </Badge>
            )}
            {warningCount > 0 && (
              <Badge variant="outline" className="text-xs text-warning border-warning/30">
                {warningCount} Warning
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {displayedAlerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Check className="h-12 w-12 text-success mb-3" />
            <p className="text-muted-foreground">No active alerts</p>
            <p className="text-xs text-muted-foreground">All clients are on track</p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayedAlerts.map((alert) => (
              <AlertItem
                key={alert.id}
                alert={alert}
                showClientName={showClientName}
                onAcknowledge={acknowledgeAlert}
                onDismiss={dismissAlert}
              />
            ))}

            {hasMore && (
              <Button variant="ghost" className="w-full text-sm" size="sm">
                View {alerts.length - maxAlerts} more alerts
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        )}

        {/* Toggle for acknowledged alerts */}
        <div className="mt-4 pt-4 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs"
            onClick={() => setShowAcknowledged(!showAcknowledged)}
          >
            {showAcknowledged ? 'Hide acknowledged alerts' : 'Show acknowledged alerts'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
